"use client"

import type { ReactNode } from "react"

import { useTranslations } from "next-intl"

import { compactCount } from "@/components/marketplace/format"
import { jumpToReviews, REVIEWS_ANCHOR } from "@/components/marketplace/pdp/review-summary"
import { CheckShieldIcon, HairlineChevronIcon, ParcelIcon, RatingStarIcon, Text } from "@/components/ui"

interface PdpShipping {
  freeShipping: boolean
}

interface PdpInfoRowProps extends PdpShipping {
  purchaseLabel?: string
  rating?: number | null
  reviewCount?: number | null
  className?: string
}

interface InfoColumnProps {
  label: string
  children: ReactNode
}

// Figma's phone row insets the purchase column's copy a pixel less than the other two.
const InfoColumn = ({ label, children }: InfoColumnProps) => (
  <div className="border-line flex min-w-0 flex-col gap-75 border-l pl-125 last:pl-100 @3xl:gap-125 @3xl:pl-225 @3xl:last:pl-225">
    <Text as="div" family="text" className="text-little @3xl:text-little-lg text-fg-heading/60 font-normal @3xl:tracking-normal">
      {label}
    </Text>
    <div className="text-little @3xl:text-little-lg flex min-w-0 items-center">{children}</div>
  </div>
)

export const PdpInfoRow = ({ freeShipping, rating, reviewCount, purchaseLabel, className }: PdpInfoRowProps) => {
  const t = useTranslations("pdp")
  return (
    <div data-testid="pdp-info-row" className={`grid-cols-pdp-info grid gap-125 @3xl:grid-cols-3 @3xl:gap-250 ${className ?? ""}`}>
      <InfoColumn label={t("info.delivery")}>
        <ParcelIcon className={`mt-50 ml-px shrink-0 opacity-90 @3xl:mt-0 ${freeShipping ? "text-fg-freeship" : "text-fg-heading"}`} aria-hidden />
        <span className={`ml-75 @3xl:ml-150 ${freeShipping ? "text-fg-shipping" : "text-fg-heading font-normal @3xl:tracking-normal"}`}>
          {freeShipping ? t("freeShipping") : t("nationwideShipping")}
        </span>
      </InfoColumn>
      {rating != null && (
        <InfoColumn label={reviewCount != null ? t("info.reviews", { count: `${compactCount(reviewCount)}` }) : t("reviews.ratingLabel")}>
          <a
            href={`#${REVIEWS_ANCHOR}`}
            onClick={jumpToReviews}
            aria-label={t("info.seeReviews")}
            className="text-fg-brand focus-visible:ring-brand rounded-2x -ml-px flex items-center pr-100 focus-visible:ring-2 focus-visible:outline-none"
          >
            <span className="tabular-nums">{rating.toFixed(1)}</span>
            <RatingStarIcon size={14} className="ml-125 size-3 shrink-0 @3xl:size-3.5" aria-hidden />
            <HairlineChevronIcon className="ml-400 shrink-0 rotate-90" aria-hidden />
          </a>
        </InfoColumn>
      )}
      <InfoColumn label={t("info.purchase")}>
        <CheckShieldIcon size={14} className="text-fg-heading mt-75 ml-px shrink-0 @3xl:mt-px" aria-hidden />
        <span className="text-fg-heading ml-125 font-normal @3xl:tracking-normal">{purchaseLabel ?? t("info.securePayment")}</span>
      </InfoColumn>
    </div>
  )
}
